import { DateTime } from "luxon";
import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";
import { TextInput, UserAvatar } from "@somethings-ui/web";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProfilePhotoDialog } from "@/components/users/user-details/profile-photo-dialog";
import {
  useUpdateEmail,
  useUpdatePhoneNumber,
  useUpdateProfilePhoto,
  type User,
} from "@/lib/api/user";
import { useUpdateUser } from "@/lib/hooks/user/use-update-user";
import { useToast } from "@/components/ui/use-toast";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  pronounOptions,
  raceOptions,
  sexOptions,
} from "@/lib/strings/profile-data";
import { isMenteeIdentity, isMentorIdentity } from "@/lib/utils/system-roles";
import { AccessControlSection } from "./access-control-section";
import {
  AccountChoiceChip,
  AccountField,
  AccountSectionCard,
} from "./account-field";
import {
  OrganizationSection,
  RecordDeactivateBar,
} from "./organization-section";
import {
  COUNTRY_CODES,
  getPhoneParts,
  isValidPhoneForSelectedCountry,
} from "@/components/auth/country-code-selector";
import { digitsOnly, formatNationalPhone } from "@/lib/utils/phone";
import { getMenteeDisplayName, REDACTED_LABEL } from "@/lib/redacted-display";
import { useRecordSaveTracker } from "./use-record-save-tracker";
import type { UserRecordSurface } from "./user-record-tabs";

export function accountFieldsReadOnly(params: {
  surface: UserRecordSurface;
  isMentor: boolean;
  canUpdateUsers: boolean;
}): boolean {
  if (params.surface === "record") return !params.canUpdateUsers;
  return params.isMentor;
}

export function photoFieldReadOnly(params: {
  surface: UserRecordSurface;
  isMentor: boolean;
  canUpdateProfileMedia: boolean;
}): boolean {
  if (params.surface === "record") return !params.canUpdateProfileMedia;
  return params.isMentor;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type OpenField =
  | "name"
  | "firstName"
  | "lastName"
  | "birthday"
  | "pronouns"
  | "sex"
  | "race"
  | "location"
  | "zipcode"
  | "phone"
  | "email"
  | "guardianName"
  | "parentPhone"
  | "parentConsent"
  | null;

interface AccountTabProps {
  user: User;
  surface: UserRecordSurface;
}

export function AccountTab({ user, surface }: AccountTabProps) {
  const ability = useAbility();
  const canUpdateUsers =
    hasPrivilegedAccessFromAbility(ability) && ability.can("update", "users");
  const canUpdateProfileMedia =
    hasPrivilegedAccessFromAbility(ability) &&
    ability.can("update", "users.profile-media");
  const { toast } = useToast();
  const trackSave = useRecordSaveTracker(surface, getMenteeDisplayName(user));
  const { handleUpdate: updateField, fieldState, form } = useUpdateUser({
    userId: user.id,
    name: user.name ?? undefined,
    firstName: user.first_name ?? undefined,
    lastName: user.last_name ?? undefined,
    pronouns: user.pronouns,
    location: user.location || "",
    guardianName: user.guardian_name ?? undefined,
    profile: {
      sex: user.profile?.sex ?? undefined,
      race: user.profile?.race ?? undefined,
      metadata: {
        zipcode: user.profile?.metadata?.zipcode,
        parentConsent: user.profile?.metadata?.parentConsent,
        parentOptOutReason: user.profile?.metadata?.parentOptOutReason,
      },
    },
  });

  const photo = useSignedMedia(user.profile_picture_url, user.id);
  const preferredName = form.watch("name") ?? "";
  const firstName = form.watch("firstName") ?? "";
  const lastName = form.watch("lastName") ?? "";
  const pronouns = form.watch("pronouns") ?? "";
  const sex = form.watch("profile.sex") ?? "";
  const race = form.watch("profile.race") ?? "";
  const location = form.watch("location") ?? "";
  const zipcode = form.watch("profile.metadata.zipcode") ?? "";
  const redactedFields = new Set(user.redacted_fields ?? []);
  const preferredNameRedacted = redactedFields.has("name");
  const locationRedacted = redactedFields.has("location");
  const guardianNameRedacted = redactedFields.has("guardian_name");
  const parentPhoneRedacted = redactedFields.has("emergency_phone");
  const emailRedacted = redactedFields.has("email");
  const guardianName = form.watch("guardianName") ?? "";
  const parentConsent = form.watch("profile.metadata.parentConsent");
  const parentOptOutReason =
    form.watch("profile.metadata.parentOptOutReason") ?? "";
  const [birthday, setBirthday] = useState(
    user.birthday
      ? DateTime.fromISO(String(user.birthday)).toISODate() ?? ""
      : "",
  );
  const [openField, setOpenField] = useState<OpenField>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const parsedPhone = getPhoneParts(user.phone ?? "");
  const parsedParentPhone = getPhoneParts(user.emergency_phone ?? "");
  const [phoneCountry, setPhoneCountry] = useState(parsedPhone.countryCode);
  const [phoneDraft, setPhoneDraft] = useState(parsedPhone.phoneNumber);
  const [parentPhoneCountry, setParentPhoneCountry] = useState(
    parsedParentPhone.countryCode,
  );
  const [parentPhoneDraft, setParentPhoneDraft] = useState(
    parsedParentPhone.phoneNumber,
  );
  const [emailDraft, setEmailDraft] = useState(user.email ?? "");
  const [contactError, setContactError] = useState("");

  const updatePhoto = useUpdateProfilePhoto(user.id);
  const updatePhone = useUpdatePhoneNumber(user.id);
  const updateEmail = useUpdateEmail(user.id);

  const birthdaySummary = birthday
    ? DateTime.fromISO(birthday).toFormat("M/d/yyyy")
    : "";
  const photoSummary = user.profile_picture_url ? "Photo added" : "No photo yet";

  const birthdayAtOpen = useRef(birthday);
  const close = () => setOpenField(null);
  const discard = (name: Parameters<typeof form.resetField>[0]) => {
    form.resetField(name);
    setOpenField(null);
  };
  const handleUpdate = async (
    field: Parameters<typeof updateField>[0],
    value: Parameters<typeof updateField>[1],
  ) => {
    const saved = await updateField(field, value);
    if (saved === false) throw new Error("save-failed");
  };
  const commitSave = async (
    save: () => Promise<unknown>,
    onSaved: () => void,
  ) => {
    try {
      await save();
    } catch (error) {
      toast({
        title: "Failed to save changes",
        variant: "destructive",
      });
      throw error;
    }
    onSaved();
  };
  const isMentor = isMentorIdentity(user.systemRoles);
  const readOnly = accountFieldsReadOnly({
    surface,
    isMentor,
    canUpdateUsers,
  });
  const photoReadOnly =
    photoFieldReadOnly({
      surface,
      isMentor,
      canUpdateProfileMedia,
    }) || redactedFields.has("profile_picture_url");
  const parentReadOnly = readOnly || !canUpdateUsers;
  const showParentDetails = isMenteeIdentity(user.systemRoles);

  const isRecord = surface === "record";

  return (
    <div className={isRecord ? undefined : "max-w-[768px]"}>
      {isRecord ? <OrganizationSection user={user} /> : null}

      <h2
        className={
          isRecord
            ? "mt-8 font-heading text-base font-bold leading-6 text-text-primary"
            : "font-heading text-base font-bold leading-6 text-text-primary"
        }
      >
        Basics
      </h2>
      <AccountSectionCard className="mt-6">
        <AccountField
          label="Photo"
          value={photoSummary}
          valueContent={
            <span className="flex items-center gap-2.5">
              <UserAvatar
                name={user.name ?? ""}
                imageUrl={photo.url}
                size={40}
              />
              <span className="font-body text-[15px] leading-[22.5px] text-text-primary">
                {photoSummary}
              </span>
            </span>
          }
          open={false}
          onOpen={() => {
            if (photoReadOnly) return;
            setPhotoOpen(true);
          }}
          onCancel={close}
          readOnly={photoReadOnly}
        />

        <AccountField
          label="Preferred name"
          value={preferredNameRedacted ? REDACTED_LABEL : preferredName}
          open={openField === "name"}
          onOpen={() => setOpenField("name")}
          onCancel={() => discard("name")}
          readOnly={readOnly || preferredNameRedacted}
          saving={fieldState.name}
          onSave={() =>
            commitSave(
              () => handleUpdate("name", preferredName),
              () => {
                form.resetField("name", { defaultValue: preferredName });
                trackSave("name");
              },
            )
          }
        >
          <TextInput
            variant="default"
            value={preferredName}
            onChange={(event) => form.setValue("name", event.target.value)}
          />
        </AccountField>

        <AccountField
          label="First name"
          value={firstName}
          open={openField === "firstName"}
          onOpen={() => setOpenField("firstName")}
          onCancel={() => discard("firstName")}
          readOnly={readOnly}
          saving={fieldState.firstName}
          onSave={() =>
            commitSave(
              () => handleUpdate("firstName", firstName),
              () => {
                form.resetField("firstName", { defaultValue: firstName });
                trackSave("first_name");
              },
            )
          }
        >
          <TextInput
            variant="default"
            value={firstName}
            onChange={(event) => form.setValue("firstName", event.target.value)}
          />
        </AccountField>

        <AccountField
          label="Last name"
          value={lastName}
          open={openField === "lastName"}
          onOpen={() => setOpenField("lastName")}
          onCancel={() => discard("lastName")}
          readOnly={readOnly}
          saving={fieldState.lastName}
          onSave={() =>
            commitSave(
              () => handleUpdate("lastName", lastName),
              () => {
                form.resetField("lastName", { defaultValue: lastName });
                trackSave("last_name");
              },
            )
          }
        >
          <TextInput
            variant="default"
            value={lastName}
            onChange={(event) => form.setValue("lastName", event.target.value)}
          />
        </AccountField>

        <AccountField
          label="Date of birth"
          value={birthdaySummary}
          open={openField === "birthday"}
          onOpen={() => {
            birthdayAtOpen.current = birthday;
            setOpenField("birthday");
          }}
          onCancel={() => {
            setBirthday(birthdayAtOpen.current);
            setOpenField(null);
          }}
          readOnly={readOnly}
          saving={fieldState.birthday}
          onSave={() =>
            commitSave(
              () => handleUpdate("birthday", birthday || undefined),
              () => {
                birthdayAtOpen.current = birthday;
                trackSave("birthday");
              },
            )
          }
        >
          <TextInput
            variant="default"
            type="date"
            value={birthday}
            onChange={(event) => setBirthday(event.target.value)}
          />
        </AccountField>

        <AccountField
          label="Pronouns"
          value={pronouns}
          open={openField === "pronouns"}
          onOpen={() => setOpenField("pronouns")}
          onCancel={() => discard("pronouns")}
          readOnly={readOnly}
          saving={fieldState.pronouns}
          onSave={() =>
            commitSave(
              () => handleUpdate("pronouns", pronouns),
              () => {
                form.resetField("pronouns", { defaultValue: pronouns });
                trackSave("pronouns");
              },
            )
          }
        >
          <div className="flex flex-wrap gap-2">
            {pronounOptions.map((option) => (
              <AccountChoiceChip
                key={option.value}
                label={option.label}
                selected={pronouns === option.value}
                onSelect={() => form.setValue("pronouns", option.value)}
              />
            ))}
          </div>
        </AccountField>

        <AccountField
          label="Sex"
          value={sex}
          open={openField === "sex"}
          onOpen={() => setOpenField("sex")}
          onCancel={() => discard("profile.sex")}
          readOnly={readOnly}
          saving={fieldState.profile}
          onSave={() =>
            commitSave(
              () =>
                handleUpdate("profile", {
                  ...form.getValues("profile"),
                  sex,
                }),
              () => {
                form.resetField("profile.sex", { defaultValue: sex });
                trackSave("sex");
              },
            )
          }
        >
          <Select
            value={sex}
            onValueChange={(value) => form.setValue("profile.sex", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select sex" />
            </SelectTrigger>
            <SelectContent>
              {sexOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </AccountField>

        <AccountField
          label="Race"
          value={race}
          open={openField === "race"}
          onOpen={() => setOpenField("race")}
          onCancel={() => discard("profile.race")}
          readOnly={readOnly}
          saving={fieldState.profile}
          onSave={() =>
            commitSave(
              () =>
                handleUpdate("profile", {
                  ...form.getValues("profile"),
                  race,
                }),
              () => {
                form.resetField("profile.race", { defaultValue: race });
                trackSave("race");
              },
            )
          }
        >
          <div className="flex flex-wrap gap-2">
            {raceOptions.map((option) => (
              <AccountChoiceChip
                key={option.value}
                label={option.label}
                selected={race === option.value}
                onSelect={() => form.setValue("profile.race", option.value)}
              />
            ))}
          </div>
        </AccountField>

        <AccountField
          label="Location"
          value={locationRedacted ? REDACTED_LABEL : location}
          open={openField === "location"}
          onOpen={() => setOpenField("location")}
          onCancel={() => discard("location")}
          readOnly={readOnly || locationRedacted}
          saving={fieldState.location}
          onSave={() =>
            commitSave(
              () => handleUpdate("location", location),
              () => {
                form.resetField("location", { defaultValue: location });
                trackSave("location");
              },
            )
          }
        >
          <TextInput
            variant="default"
            value={location}
            placeholder="Enter location"
            onChange={(event) => form.setValue("location", event.target.value)}
          />
        </AccountField>

        <AccountField
          label="Zip code"
          value={zipcode}
          open={openField === "zipcode"}
          onOpen={() => setOpenField("zipcode")}
          onCancel={() => discard("profile.metadata.zipcode")}
          readOnly={readOnly}
          saving={fieldState.profile}
          onSave={() =>
            commitSave(
              () =>
                handleUpdate("profile", {
                  ...form.getValues("profile"),
                  metadata: {
                    ...form.getValues("profile.metadata"),
                    zipcode,
                  },
                }),
              () => {
                form.resetField("profile.metadata.zipcode", {
                  defaultValue: zipcode,
                });
                trackSave("zipcode");
              },
            )
          }
        >
          <TextInput
            variant="default"
            value={zipcode}
            placeholder="Enter zip code"
            onChange={(event) =>
              form.setValue("profile.metadata.zipcode", event.target.value)
            }
          />
        </AccountField>
      </AccountSectionCard>

      <h2 className="mt-8 font-heading text-base font-bold leading-6 text-text-primary">
        Account
      </h2>
      <AccountSectionCard className="mt-6">
        <AccountField
          label="Phone number"
          value={formatNationalPhone(user.phone ?? "")}
          open={openField === "phone"}
          onOpen={() => {
            const next = getPhoneParts(user.phone ?? "");
            setPhoneCountry(next.countryCode);
            setPhoneDraft(next.phoneNumber);
            setContactError("");
            setOpenField("phone");
          }}
          onCancel={close}
          readOnly={readOnly}
          saving={updatePhone.isPending}
          saveLabel="Text a code"
          cancelAsText
          onSave={async () => {
            const fullPhoneNumber = `${phoneCountry}${phoneDraft}`;
            if (
              !isValidPhoneForSelectedCountry(fullPhoneNumber, phoneCountry)
            ) {
              setContactError("Enter a valid phone number");
              throw new Error("invalid-phone");
            }
            setContactError("");
            await commitSave(
              () => updatePhone.mutateAsync(fullPhoneNumber),
              () => trackSave("phone"),
            );
          }}
        >
          <div className="flex h-14 items-center overflow-hidden rounded-xl border border-[#8a8275]">
            <Select
              value={phoneCountry}
              onValueChange={setPhoneCountry}
            >
              <SelectTrigger className="h-full w-[88px] border-0 bg-transparent shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.map((item) => (
                  <SelectItem key={item.iso} value={item.code}>
                    {item.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="h-8 w-px shrink-0 bg-[#dfddd8]" />
            <input
              type="tel"
              value={phoneDraft}
              onChange={(event) => {
                setPhoneDraft(digitsOnly(event.target.value));
                setContactError("");
              }}
              placeholder="3332345678"
              className="h-full min-w-0 flex-1 bg-transparent px-3 font-body text-sm text-text-primary outline-none"
            />
          </div>
          <p className="mt-2 flex items-center gap-1 font-body text-sm text-[#58534b]">
            <ChevronDown className="size-3.5" />
            We&apos;ll text a code to confirm it&apos;s yours
          </p>
          {openField === "phone" && contactError ? (
            <p className="mt-1 font-body text-sm text-states-error">
              {contactError}
            </p>
          ) : null}
        </AccountField>
        <AccountField
          label="Email"
          value={emailRedacted ? REDACTED_LABEL : (user.email ?? "")}
          open={openField === "email"}
          onOpen={() => {
            setEmailDraft(user.email ?? "");
            setContactError("");
            setOpenField("email");
          }}
          onCancel={close}
          readOnly={readOnly || emailRedacted}
          saving={updateEmail.isPending}
          cancelAsText
          onSave={async () => {
            if (!EMAIL_PATTERN.test(emailDraft.trim())) {
              setContactError("Enter a valid email");
              throw new Error("invalid-email");
            }
            setContactError("");
            await commitSave(
              () => updateEmail.mutateAsync(emailDraft.trim()),
              () => trackSave("email"),
            );
          }}
        >
          <TextInput
            variant="default"
            type="email"
            value={emailDraft}
            onChange={(event) => {
              setEmailDraft(event.target.value);
              setContactError("");
            }}
          />
          {openField === "email" && contactError ? (
            <p className="mt-2 font-body text-sm text-states-error">
              {contactError}
            </p>
          ) : null}
        </AccountField>
      </AccountSectionCard>

      {showParentDetails ? (
        <>
          <h2 className="mt-8 font-heading text-base font-bold leading-6 text-text-primary">
            Parent Details
          </h2>
          <AccountSectionCard className="mt-6">
            <AccountField
              label="Name"
              value={guardianNameRedacted ? REDACTED_LABEL : guardianName}
              open={openField === "guardianName"}
              onOpen={() => setOpenField("guardianName")}
              onCancel={() => discard("guardianName")}
              readOnly={parentReadOnly || guardianNameRedacted}
              saving={fieldState.guardianName}
              onSave={() =>
                commitSave(
                  () => handleUpdate("guardianName", guardianName),
                  () => {
                    form.resetField("guardianName", {
                      defaultValue: guardianName,
                    });
                    trackSave("guardian_name");
                  },
                )
              }
            >
              <TextInput
                variant="default"
                value={guardianName}
                placeholder="Enter parent name"
                onChange={(event) =>
                  form.setValue("guardianName", event.target.value)
                }
              />
            </AccountField>

            <AccountField
              label="Phone number"
              value={
                parentPhoneRedacted
                  ? REDACTED_LABEL
                  : formatNationalPhone(user.emergency_phone ?? "")
              }
              open={openField === "parentPhone"}
              onOpen={() => {
                const next = getPhoneParts(user.emergency_phone ?? "");
                setParentPhoneCountry(next.countryCode);
                setParentPhoneDraft(next.phoneNumber);
                setContactError("");
                setOpenField("parentPhone");
              }}
              onCancel={close}
              readOnly={parentReadOnly || parentPhoneRedacted}
              saving={fieldState.emergencyPhone}
              onSave={async () => {
                if (!parentPhoneDraft) {
                  setContactError("");
                  await commitSave(
                    () => handleUpdate("emergencyPhone", ""),
                    () => trackSave("parent_phone"),
                  );
                  return;
                }
                const fullPhoneNumber = `${parentPhoneCountry}${parentPhoneDraft}`;
                if (
                  !isValidPhoneForSelectedCountry(
                    fullPhoneNumber,
                    parentPhoneCountry,
                  )
                ) {
                  setContactError("Enter a valid phone number");
                  throw new Error("invalid-phone");
                }
                setContactError("");
                await commitSave(
                  () => handleUpdate("emergencyPhone", fullPhoneNumber),
                  () => trackSave("parent_phone"),
                );
              }}
            >
              <div className="flex h-14 items-center overflow-hidden rounded-xl border border-[#8a8275]">
                <Select
                  value={parentPhoneCountry}
                  onValueChange={setParentPhoneCountry}
                >
                  <SelectTrigger className="h-full w-[88px] border-0 bg-transparent shadow-none focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((item) => (
                      <SelectItem key={item.iso} value={item.code}>
                        {item.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="h-8 w-px shrink-0 bg-[#dfddd8]" />
                <input
                  type="tel"
                  value={parentPhoneDraft}
                  onChange={(event) => {
                    setParentPhoneDraft(digitsOnly(event.target.value));
                    setContactError("");
                  }}
                  placeholder="3332345678"
                  className="h-full min-w-0 flex-1 bg-transparent px-3 font-body text-sm text-text-primary outline-none"
                />
              </div>
              {openField === "parentPhone" && contactError ? (
                <p className="mt-1 font-body text-sm text-states-error">
                  {contactError}
                </p>
              ) : null}
            </AccountField>

            <AccountField
              label="Email"
              value={emailRedacted ? REDACTED_LABEL : (user.email ?? "")}
              open={false}
              onOpen={() => undefined}
              onCancel={close}
              readOnly
            />

            <AccountField
              label="Parent Consent"
              value={parentConsent ?? ""}
              open={openField === "parentConsent"}
              onOpen={() => setOpenField("parentConsent")}
              onCancel={() => discard("profile.metadata.parentConsent")}
              readOnly={parentReadOnly}
              saving={fieldState.profile}
              onSave={() =>
                commitSave(
                  () =>
                    handleUpdate("profile", {
                      ...form.getValues("profile"),
                      metadata: {
                        ...form.getValues("profile.metadata"),
                        parentConsent,
                        parentOptOutReason,
                      },
                    }),
                  () => {
                    form.resetField("profile.metadata.parentConsent", {
                      defaultValue: parentConsent,
                    });
                    trackSave("parent_consent");
                  },
                )
              }
            >
              <div className="flex flex-wrap gap-2">
                {(["Yes", "No"] as const).map((option) => (
                  <AccountChoiceChip
                    key={option}
                    label={option}
                    selected={parentConsent === option}
                    onSelect={() =>
                      form.setValue("profile.metadata.parentConsent", option)
                    }
                  />
                ))}
              </div>
              <p className="mt-2 font-body text-sm text-[#58534b]">
                Edit with care. This governs what we may send about this user.
              </p>
            </AccountField>

            <AccountField
              label="Opt-out Reason"
              value={
                parentOptOutReason ||
                "Applies once consent is declined or withdrawn"
              }
              open={false}
              onOpen={() => undefined}
              onCancel={close}
              readOnly
            />
          </AccountSectionCard>
        </>
      ) : null}

      {showParentDetails && isRecord ? (
        <AccessControlSection user={user} />
      ) : null}

      <ProfilePhotoDialog
        open={photoOpen && !photoReadOnly}
        onClose={() => setPhotoOpen(false)}
        userName={user.name ?? undefined}
        currentImageUrl={photo.url}
        onUpload={async (file) => {
          await updatePhoto.mutateAsync(file);
          trackSave("profile_photo");
        }}
      />

      {isRecord ? <RecordDeactivateBar user={user} /> : null}
    </div>
  );
}
